/**
 * Cart Group Monitor
 * - Protects setup fee from deletion (server-side via Liquid, client-side backup here)
 * - Auto-removes setup fee when group meets minimum threshold
 * - Auto-re-adds setup fee when group drops below minimum
 * - Syncs addon qty when main product items change
 *
 * Reads config from a <script id="cart-group-config"> JSON block injected by Liquid.
 */
(function() {
  'use strict';
  if (window.__diyCartMonitorLoaded) return;
  window.__diyCartMonitorLoaded = true;

  const CART_URL = window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart.js' : '/cart.js';
  const CART_CHANGE_URL = window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart/change.js' : '/cart/change.js';
  const CART_ADD_URL = window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js';

  let isProcessing = false;
  let debounceTimer = null;

  /* ── Loading overlay ── */
  function showLoader(msg) {
    let el = document.getElementById('cart-group-loader');
    if (!el) {
      el = document.createElement('div');
      el.id = 'cart-group-loader';
      el.innerHTML = '<div class="cgl-box"><div class="cgl-spinner"></div><p class="cgl-msg"></p></div>';
      const style = document.createElement('style');
      style.textContent = `
        #cart-group-loader{position:fixed;inset:0;z-index:999999;background:rgba(0,0,0,0.45);backdrop-filter:blur(3px);display:flex;align-items:center;justify-content:center;opacity:0;transition:opacity .25s}
        #cart-group-loader.is-visible{opacity:1}
        .cgl-box{background:#fff;border-radius:16px;padding:32px 40px;text-align:center;box-shadow:0 20px 50px rgba(0,0,0,0.2);max-width:340px;width:90%}
        .cgl-spinner{width:36px;height:36px;border:3px solid rgba(0,0,0,0.1);border-top-color:#FF5000;border-radius:50%;animation:cgl-spin .7s linear infinite;margin:0 auto 16px}
        @keyframes cgl-spin{to{transform:rotate(360deg)}}
        .cgl-msg{margin:0;font-size:0.9375rem;font-weight:600;color:#111;line-height:1.4}
      `;
      document.head.appendChild(style);
      document.body.appendChild(el);
    }
    el.querySelector('.cgl-msg').textContent = msg || 'Updating cart...';
    el.classList.add('is-visible');
  }

  function hideLoader() {
    const el = document.getElementById('cart-group-loader');
    if (el) el.classList.remove('is-visible');
  }

  async function getCart() {
    const res = await fetch(CART_URL, { headers: { 'Accept': 'application/json' } });
    return res.json();
  }

  async function changeItem(line, quantity) {
    const res = await fetch(CART_CHANGE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
    return res.json();
  }

  async function addItem(id, quantity, properties) {
    const res = await fetch(CART_ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id, quantity, properties }] })
    });
    return res.json();
  }

  function getProp(item, key) {
    if (!item.properties) return null;
    return item.properties[key] || null;
  }

  async function syncCart() {
    // The cart page performs group updates atomically itself.
    if (document.querySelector('[data-eb-cart]') || window.__diyCartBusy) return;
    if (isProcessing) return;
    isProcessing = true;

    try {
      const cart = await getCart();
      const configEl = document.getElementById('cart-group-config');
      let config = { minimum_qty: 24, setup_fee_variant_id: '' };
      if (configEl) {
        try { config = JSON.parse(configEl.textContent); } catch(e) {}
      }

      const minQty = config.minimum_qty || 24;
      const setupFeeVariantId = config.setup_fee_variant_id || '';

      // Group items by _Group property
      const groups = {};
      cart.items.forEach((item, idx) => {
        const group = getProp(item, '_Group');
        if (!group) return;
        if (!groups[group]) groups[group] = { main: [], addons: [], setupFee: null, setupFeeIndex: -1 };

        const isProtected = getProp(item, '_Protected') === 'true';
        const isAddon = getProp(item, '_For') != null && !isProtected;

        if (isProtected) {
          groups[group].setupFee = item;
          groups[group].setupFeeIndex = idx + 1; // 1-indexed for Shopify API
        } else if (isAddon) {
          groups[group].addons.push({ item, line: idx + 1 });
        } else {
          groups[group].main.push({ item, line: idx + 1 });
        }
      });

      // Use /cart/update.js to batch all changes in one call
      const updates = {};
      let needsAdd = null;

      for (const [groupId, group] of Object.entries(groups)) {
        const mainQty = group.main.reduce((sum, m) => sum + m.item.quantity, 0);

        // 1. Setup fee management (only when setup fee is configured)
        if (setupFeeVariantId) {
          if (mainQty >= minQty && group.setupFee) {
            console.log(`[CartMonitor] Group ${groupId}: ${mainQty} >= ${minQty}, removing setup fee`);
            updates[group.setupFee.key] = 0;
          } else if (mainQty > 0 && mainQty < minQty && !group.setupFee) {
            console.log(`[CartMonitor] Group ${groupId}: ${mainQty} < ${minQty}, will add setup fee`);
            needsAdd = {
              id: parseInt(setupFeeVariantId, 10),
              quantity: 1,
              properties: {
                '_Group': groupId,
                '_For': group.main[0]?.item.product_title || 'Product',
                '_Protected': 'true'
              }
            };
          }
        }

        // 2. Addon qty sync
        for (const addon of group.addons) {
          if (mainQty > 0 && addon.item.quantity !== mainQty) {
            console.log(`[CartMonitor] Group ${groupId}: syncing addon qty ${addon.item.quantity} → ${mainQty}`);
            updates[addon.item.key] = mainQty;
          } else if (mainQty <= 0) {
            console.log(`[CartMonitor] Group ${groupId}: removing orphaned addon`);
            updates[addon.item.key] = 0;
          }
        }

        // 3. Remove setup fee if no main items left
        if (mainQty <= 0 && group.setupFee) {
          console.log(`[CartMonitor] Group ${groupId}: removing orphaned setup fee`);
          updates[group.setupFee.key] = 0;
        }
      }

      let cartChanged = false;

      // Apply batch updates
      if (Object.keys(updates).length > 0) {
        // Determine what kind of cleanup is happening for the loader message
        const hasOrphanRemoval = Object.values(updates).some(v => v === 0);
        if (hasOrphanRemoval) {
          showLoader('Removing customized items from cart...');
        } else {
          showLoader('Syncing your order...');
        }

        await fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });
        cartChanged = true;
      }

      // Add setup fee (can't batch with updates)
      if (needsAdd) {
        if (!cartChanged) showLoader('Updating your order...');
        await addItem(needsAdd.id, needsAdd.quantity, needsAdd.properties);
        cartChanged = true;
      }

      if (cartChanged) {
        window.location.reload();
      } else {
        hideLoader();
      }
    } catch (err) {
      console.error('[CartMonitor] Error:', err);
      hideLoader();
    } finally {
      isProcessing = false;
    }
  }

  // Debounced sync — runs after cart updates
  function debouncedSync() {
    if (window.__diyCartBusy || document.querySelector('[data-eb-cart]')) return;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(syncCart, 1500);
  }

  // Listen for cart updates from the theme's cart system
  document.addEventListener('cart:update', debouncedSync);
  document.addEventListener('cart-update', debouncedSync);

  // Also listen for Horizon theme's custom events
  document.addEventListener('theme:cart:update', debouncedSync);

  // MutationObserver to detect DOM changes in cart (backup for themes that don't fire events)
  function observeCart() {
    const cartForm = document.querySelector('.cart-form, #cart-form');
    if (!cartForm) return;

    const observer = new MutationObserver(() => {
      debouncedSync();
    });

    observer.observe(cartForm, { childList: true, subtree: true });
  }

  // Edit order handler — saves group state, removes group, redirects with pre-fill data
  document.addEventListener('click', async function(e) {
    const editLink = e.target.closest('[data-edit-group]');
    if (!editLink) return;
    e.preventDefault();

    const groupId = editLink.getAttribute('data-edit-group');
    const productUrl = editLink.getAttribute('href');
    if (!groupId || !productUrl) return;

    editLink.textContent = 'Loading...';
    editLink.style.pointerEvents = 'none';

    try {
      // Fetch cart and extract group state before removing
      const cart = await getCart();
      const editState = { variants: {}, addons: [], artwork: {}, color: '' };
      const updates = {};

      cart.items.forEach(function(item) {
        var group = item.properties && item.properties['_Group'];
        if (group !== groupId) return;
        updates[item.key] = 0;

        var isProtected = item.properties && item.properties['_Protected'] === 'true';
        var isAddon = item.properties && item.properties['_For'] && !isProtected;

        if (isProtected) return; // skip setup fee

        if (isAddon) {
          // Save addon variant ID
          editState.addons.push(item.variant_id.toString());
        } else {
          // Main product — save variant qty and extract color
          editState.variants[item.variant_id.toString()] = item.quantity;
          // Get color from variant title (e.g. "Navy / L" → "Navy")
          if (!editState.color && item.variant_title) {
            var parts = item.variant_title.split(' / ');
            if (parts.length > 1) editState.color = parts[0].trim();
          }
        }

        // Save artwork URLs from properties
        if (item.properties) {
          Object.keys(item.properties).forEach(function(key) {
            if (key.indexOf('Artwork') === 0 && item.properties[key]) {
              var location = key.replace('Artwork - ', '').replace('Artwork-', '').trim();
              editState.artwork[location] = item.properties[key];
            }
          });
        }
      });

      // Save state to sessionStorage
      try { sessionStorage.setItem('sixsis_edit_order', JSON.stringify(editState)); } catch(e) {}

      // Remove all items in the group
      if (Object.keys(updates).length > 0) {
        await fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates })
        });
      }

      // Redirect to product page with edit flag
      window.location.href = productUrl + (productUrl.includes('?') ? '&' : '?') + 'edit=1';
    } catch (err) {
      console.error('[CartMonitor] Edit error:', err);
      editLink.textContent = 'Edit order';
      editLink.style.pointerEvents = '';
    }
  });

  // Intercept fetch calls to cart APIs to trigger sync after changes
  const origFetch = window.fetch;
  window.fetch = function() {
    const args = arguments;
    const result = origFetch.apply(this, args);
    const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
    if (url.includes('/cart/change') || url.includes('/cart/update') || url.includes('/cart/add')) {
      result.then(() => { debouncedSync(); }).catch(() => {});
    }
    return result;
  };

  // Run on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      syncCart();
      observeCart();
    });
  } else {
    syncCart();
    observeCart();
  }
})();
